<template>
    <div v-if="amazonLayers">
        <div class="row align-center">
            <div class="col-12">
                <div class="d-flex flex-column">
                    <h6 class="font-weight-bold no-wrap align-center">Amazon Custom Template</h6>
                    <div class="list-group-item d-inline-flex border-0 px-0 disabled">
                        <span>{{ amazonHeader.name }}</span>
                    </div>
                    <div class="mt-3 mb-3">
                        <span class="mb-2 d-block">Surface</span>
                        <c-select
                            v-if="surfaces.length > 1"
                            class="flex-fill w-50"
                            :value="selectedSurface"
                            :options="surfaces"
                            label="Select surface"
                            label-class="text-bold mb-2"
                            @input="onSurfaceChange"
                        />
                        <div v-else class="text-muted">
                            {{ surfaces[0]?.label || 'No surfaces found' }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row mt-4 align-center">
            <div class="col-12 col-sm-7">
                <h6 class="font-weight-bold no-wrap align-center">Select elements to import</h6>
            </div>
            <div class="col-12 col-sm-5">
                <modal-import-search @search-text-change="updateSearchText" />
            </div>
        </div>

        <div class="mt-2 mb-2">
            <div class="col-12 layer-tree">
                <div v-if="showLoader" class="align-items-center d-flex justify-content-center my-2 text-center my-2">
                    <b-spinner variant="primary" />
                    <strong class="ml-3 primary-text">{{ loaderMessage }}</strong>
                </div>
                <template v-else>
                    <modal-import-layers-tree :tree-item="filteredTreeItems" />
                    <b-pagination
                        v-model="currentPage"
                        class="mt-4"
                        :total-rows="pageElementsCount"
                        :per-page="perPage"
                        aria-controls="my-table"
                        align="center"
                    />
                </template>
            </div>
        </div>

        <div class="mt-4">
            <h6 class="font-weight-bold">Template Info</h6>
            <div class="text-muted small">
                <div>Components: {{ templateStats.components }}</div>
                <div>Canvas Objects: {{ templateStats.canvasObjects }}</div>
                <div>Bindings: {{ templateStats.bindings }}</div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useFileLayers } from '@/composables/externalFileParser';
import store from '@/store';
import { computed, onMounted, ref, watch } from 'vue';
import { TreeItem, TreeType } from '@/components/Layers/modal/types';
import ModalImportSearch from '@/components/Layers/modal/import/search/ModalImportSearch.vue';
import ModalImportLayersTree from '@/components/Layers/modal/import/layers-tree/ModalImportLayersTree.vue';
import CSelect from '@/components/shared/CSelect/CSelect.vue';
import type { CSelectItem, Primitive } from '@/components/shared/CSelect';
import type { FileParserType } from '@/typings/parsers/fileParserTypes';

const props = defineProps<{
    fileType?: FileParserType;
}>();

const showLoader = ref(false);
const loaderMessage = ref('Loading...');
const searchText = ref('');
const perPage = 8;
const currentPage = ref(1);

const fileBuffer = computed(() => store.state.storeLayersModal?.fileBuffer);
const { data: amazonLayers, parseAndExtractLayers } = useFileLayers(props.fileType ?? 'AMAZON_FILE_XLSX');

const amazonHeader = computed(() => ({
    id: '',
    name: amazonLayers.value?.artworkName || 'Amazon Template',
    mode: amazonLayers.value?.mode
}));

const surfaces = computed<CSelectItem<number>[]>(
    () =>
        amazonLayers.value?.pages.map((page) => ({
            label: `${page.name}`,
            value: page.pageNumber
        })) || []
);

const selectedSurface = ref<CSelectItem<number>>(surfaces.value[0]);

const selectedPage = computed(() => selectedSurface.value?.value || 1);

const page = computed(() => amazonLayers.value?.pages.find((page) => page.pageNumber === selectedPage.value) || null);

watch(selectedPage, (newPage) => {
    if (!amazonLayers.value || !newPage) return;
    amazonLayers.value.selectedPage = newPage;
    // Update the store when page changes
    store.dispatch('storeLayersModal/setParsedFileLayers', amazonLayers.value);
});

const treeItemChildren = computed(() =>
    page.value
        ? page.value.elements.map(
              (element, index): TreeItem => ({
                  id: index + 1,
                  name: element.name,
                  icon: '',
                  type: element.type as TreeType,
                  metadata: element
              })
          )
        : []
);

const treeItemElements = computed(() => {
    const startIndex = (currentPage.value - 1) * perPage;
    const endIndex = startIndex + perPage;

    const childItems: TreeItem = {
        name: 'Select All',
        icon: '',
        type: TreeType.FROM_FILE_ELEMENT,
        paginatedChildren: treeItemChildren.value.slice(startIndex, endIndex) || [],
        children: treeItemChildren.value || []
    };
    return childItems;
});

const filteredTreeItems = computed(() => {
    const startIndex = (currentPage.value - 1) * perPage;
    const endIndex = startIndex + perPage;
    const { children: childrenTreeItems } = treeItemElements.value;

    if (!childrenTreeItems || !searchText.value) {
        return treeItemElements.value;
    }

    const filteredTreeItems = [...childrenTreeItems].filter((childTreeType) => {
        if (!childTreeType.name) return false;
        return childTreeType.name.toLowerCase().includes(searchText.value.toLowerCase());
    });

    const result = {
        ...treeItemElements.value,
        children: filteredTreeItems,
        paginatedChildren: filteredTreeItems.slice(startIndex, endIndex) || []
    };
    return result;
});

const pageElementsCount = computed(() => filteredTreeItems.value.children?.length || 0);

const templateStats = computed(() => {
    if (!page.value) {
        return { components: 0, canvasObjects: 0, bindings: 0 };
    }

    // Count elements by type
    const elements = page.value.elements || [];
    const canvasObjects = elements.filter(
        (el) => el.type === TreeType.FROM_FILE_TEXT_ELEMENT || el.type === TreeType.FROM_FILE_IMAGE_ELEMENT
    ).length;
    const components = elements.length - canvasObjects;

    return {
        components,
        canvasObjects,
        bindings: 0 // Will be calculated from customData if available
    };
});

onMounted(async () => {
    if (amazonLayers.value) {
        showLoader.value = false;
        return;
    }
    await parseAmazonFile();
});

const parseAmazonFile = async () => {
    try {
        showLoader.value = true;
        loaderMessage.value = 'Loading Amazon template...';
        await parseAndExtractLayers(fileBuffer.value!);

        // Store the parsed layers in the store for the import modal to access
        if (amazonLayers.value) {
            store.dispatch('storeLayersModal/setParsedFileLayers', amazonLayers.value);
        }

        showLoader.value = false;
    } catch (error) {
        console.error('Error parsing Amazon file:', error);
        showLoader.value = false;
    }
};

const updateSearchText = (value: string) => {
    searchText.value = value;
    currentPage.value = 1;
};

const onSurfaceChange = (newSurface?: CSelectItem<Primitive> | Primitive) => {
    if (!newSurface) return;
    if (typeof newSurface === 'object') {
        selectedSurface.value = newSurface as CSelectItem<number>;
    } else if (typeof newSurface === 'number') {
        const surface = surfaces.value.find((s) => s.value === newSurface);
        if (surface) {
            selectedSurface.value = surface;
        }
    }
};

watch(
    () => surfaces.value,
    (newSurfaces) => {
        if (newSurfaces.length > 0 && !selectedSurface.value) {
            selectedSurface.value = newSurfaces[0];
        }
    }
);
</script>

<style scoped>
.layer-tree {
    max-height: 400px;
    overflow-y: auto;
}
</style>
